<template>
<div style="display:grid;grid-template-rows:32px auto;grid-row-gap:12px;width:100%;height:100%;">
  <div style="display:flex;justify-content:center;min-width:0;min-height:0">
    <select-ctrl-group v-if="showBackBtn" :ids="selectedNames" :options="selectedOpts" id-prop="name" />
  </div>
  <div style="min-width:0;min-height:0;">
    <e-chart
      ref="chart"
      :showBackBtn="showBackBtn"
      @initialized="onInitialized"
      @destroy="onDestroy"
      @back="onBackBtnClick"
    />
  </div>
</div>
</template>

<script>
import EChart from '../Charts/ECharts'
import SelectCtrlGroup from '../SelectCtrlGroup/index'

export default {
  name: 'Pm25Ion',
  components: {SelectCtrlGroup, EChart},
  props: {
    siteMap: {
      required: true,
      type: Object
    },
    factorMap: {
      required: true,
      type: Object
    },
    query: {
      required: true,
      type: Object
    },
    dataList: {
      required: true,
      type: Array
    }
  },
  data: () => ({
    showBackBtn: false,
    selectedNames: [],
    selectedOpts: Object.freeze([])
  }),
  methods: {
    onLegendSelectChanged (param) {
      this.selected = Object.freeze(JSON.parse(JSON.stringify(param.selected)))
      this.selectedNames = Object.entries(this.selected).filter(selected => selected[1]).map(selected => selected[0])
    },
    onInitialized (chartObj) {
      this.inited = true
      chartObj.on('click', this.onClick)
      chartObj.on('legendselectchanged', this.onLegendSelectChanged)
      this.setChartOption(this.dataList)
    },
    onDestroy (chartObj) {
      chartObj.off('click', this.onClick)
      chartObj.off('legendselectchanged', this.onLegendSelectChanged)
    },
    setChartOption (dataList) {
      let {query} = this

      if (Object.values(query).length === 0) {
        return
      }

      let beginTS = new Date(query.beginTime).getTime()
      let endTS = new Date(query.endTime).getTime()
      let timeInterval = query.dataType * 60000
      let dataTimes = []
      this.showBackBtn = false

      for (let i = beginTS; i <= endTS; i += timeInterval) {
        dataTimes.push(new Date(i).format())
      }

      let dataTimesLen = dataTimes.length
      let {siteMap, factorMap} = this
      let {siteIds, factorIds} = query
      let dataMap = siteIds.reduce((map, siteId) => {
        let site = siteMap[siteId]
        let siteName = site.name

        let siteIonSumId = `${siteId}-ion`
        map[siteIonSumId] = {
          id: siteIonSumId,
          name: `${siteName}-离子加和`,
          type: 'line',
          data: new Array(dataTimesLen).fill(null),
          showSymbol: false,
          symbolSize: 16
        }

        return factorIds.reduce((map, factorId) => {
          let factor = factorMap[factorId]
          let id = `${siteId}-${factorId}`
          let seriesObj = {
            id,
            name: `${siteName}-${factor.name}`,
            data: new Array(dataTimesLen).fill(null),
            showSymbol: false
          }

          if (factorId === '1008') {
            seriesObj.type = 'line'
          } else {
            seriesObj.type = 'bar'
            seriesObj.stack = siteId
          }

          map[id] = seriesObj

          return map
        }, map)
      }, {})

      let dataListLen = dataList.length

      for (let i = 0; i < dataListLen; i++) {
        let data = dataList[i]
        let {siteId, factorId} = data
        let seriesId = `${siteId}-${factorId}`
        let series = dataMap[seriesId]

        if (series) {
          let seriesData = series.data
          let index = (new Date(data.dataTime).getTime() - beginTS) / timeInterval
          let {value} = data

          seriesData[index] = value

          if (factorId !== '1008') {
            let sumIonSeries = dataMap[`${siteId}-ion`]

            if (sumIonSeries) {
              let sumIonSeriesData = sumIonSeries.data
              let sumIonValue = sumIonSeriesData[index]
              sumIonSeriesData[index] = Number.isFinite(sumIonValue) ? (sumIonValue + value) : value
            }
          }
        }
      }

      let series = Object.values(dataMap)

      series.forEach(series => {
        series.data.forEach((value, index, values) => {
          if (Number.isFinite(value)) {
            values[index] = Number(value.toFixed(3))
          }
        })
      })

      let chartOpt = {
        toolbox: {
          feature: {
            saveAsImage: {}
          },
          top: -6,
          right: 0
        },
        tooltip: {
          trigger: 'axis',
          appendToBody: true,
          axisPointer: {
            type: 'cross',
            label: {
              backgroundColor: 'rgba(0, 0, 0, .5)'
            }
          }
        },
        legend: {
          top: 10,
          left: 'center',
          type: 'scroll',
          width: '80%',
          ...this.getMainLegendData(siteIds, siteMap, factorIds, factorMap)
        },
        grid: {
          top: 70,
          right: 20,
          bottom: 50,
          left: 20,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: dataTimes,
          axisTick: {
            alignWithLabel: true
          }
        },
        yAxis: {
          type: 'value',
          name: 'μg/m3',
          axisLine: {
            show: true
          },
          axisTick: {
            show: true
          }
        },
        dataZoom: [{
          type: 'inside',
          start: dataTimes.length <= 72 ? 0 : ((dataTimes.length - 72) / dataTimes.length * 100).toFixed(0),
          end: 100,
          realtime: true,
          xAxisIndex: 0,
          minSpan: 5
        }, {
          type: 'slider',
          start: dataTimes.length <= 72 ? 0 : ((dataTimes.length - 72) / dataTimes.length * 100).toFixed(0),
          end: 100,
          realtime: true,
          xAxisIndex: 0,
          minSpan: 5,
          // height: 39,
          bottom: 9,
          left: 'center',
          handleIcon: 'M10.7,11.9H9.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4h1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
          handleSize: '70%',
          handleStyle: {
            color: '#fff',
            shadowBlur: 4,
            shadowColor: 'rgba(0, 0, 0, 0.15)',
            shadowOffsetX: 2,
            shadowOffsetY: 2
          },
          // dataBackground: {
          //   areaStyle: '#E4F0FF'
          // }
          fillerColor: 'rgba(228, 240, 255, .6)',
          borderColor: '#E4F0FF'
        }],
        series: Object.values(dataMap)
      }

      this.$refs.chart.setOpt(chartOpt)
    },
    setSelectedCtrlData (selected) {
      let entries = Object.entries(selected)
      let len = entries.length
      let selectedNames = []
      let selectedOpts = []

      for (let i = 0; i < len; i++) {
        let item = entries[i]

        if (item[1]) {
          selectedNames.push(item[0])
          selectedOpts.push({
            name: item[0]
          })
        }
      }

      this.selectedNames = selectedNames
      this.selectedOpts = Object.freeze(selectedOpts)
    },
    getMainLegendData (siteIds, siteMap, factorIds, factorMap) {
      let selected = siteIds.reduce((map, siteId) => {
        let siteName = siteMap[siteId].name
        map[`${siteName}-${factorMap['1008'].name}`] = true
        map[`${siteName}-离子加和`] = true

        return factorIds.reduce((map, factorId) => {
          if (factorId !== '1008') {
            map[`${siteName}-${factorMap[factorId].name}`] = false
          }

          return map
        }, map)
      }, {})

      this.setSelectedCtrlData(selected)

      return {
        data: siteIds.reduce((list, siteId) => {
          let site = siteMap[siteId]
          let siteName = site.name
          list.push(`${siteName}-${factorMap['1008'].name}`)
          list.push(`${siteName}-离子加和`)

          return list
        }, []),
        selected
      }
    },
    getSubLegendData (siteIds, siteMap, factorIds, factorMap) {
      let selected = siteIds.reduce((map, siteId) => {
        let siteName = siteMap[siteId].name
        map[`${siteName}-离子加和`] = false

        return factorIds.reduce((map, factorId) => {
          map[`${siteName}-${factorMap[factorId].name}`] = true
          return map
        }, map)
      }, {})

      this.setSelectedCtrlData(selected)

      return {
        data: siteIds.reduce((list, siteId) => {
          let siteName = siteMap[siteId].name
          return factorIds.reduce((list, factorId) => {
            list.push(`${siteName}-${factorMap[factorId].name}`)
            return list
          }, list)
        }, []),
        selected
      }
    },
    onClick (event) {
      if (/-ion$/.test(event.seriesId)) {
        let chartOpt = this.$refs.chart.getOpt()
        let {query} = this
        chartOpt.legend[0] = Object.assign(chartOpt.legend[0], this.getSubLegendData(query.siteIds, this.siteMap, query.factorIds, this.factorMap))
        this.$refs.chart.setOpt(chartOpt)
        this.showBackBtn = true
      }
    },
    onBackBtnClick () {
      this.showBackBtn = false
      let {query} = this
      let chartOpt = this.$refs.chart.getOpt()
      chartOpt.legend[0] = Object.assign(chartOpt.legend[0], this.getMainLegendData(query.siteIds, this.siteMap, query.factorIds, this.factorMap))
      this.$refs.chart.setOpt(chartOpt)
    },
    getSelected () {
      let factorIdMap = this.selectedNames.reduce((map, name) => {
        map[name] = true
        return map
      }, {})

      this.selectedOpts.reduce((map, {name}) => {
        if (!map[name]) {
          map[name] = false
        }

        return map
      }, factorIdMap)

      return factorIdMap
    }
  },
  watch: {
    dataList (newVal, oldVal) {
      if (this.inited) {
        this.setChartOption(newVal, newVal === oldVal)
      }
    },
    selectedNames (selectedNames, old) {
      if (selectedNames !== old) {
        return
      }

      let chartObj = this.$refs.chart
      let chartOpt = chartObj.getOpt()

      if (!chartOpt) {
        return
      }

      chartObj.setOpt({
        legend: {
          selected: this.getSelected()
        }
      }, true)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Data/Pm25Ion.vue